import { FC, memo, useMemo, ReactNode, useState, useCallback } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Badge from "@mui/material/Badge";
import Chip from "@mui/material/Chip";
import type { FilterCategory, FilterKey, FilterSection } from "../types";
import { FILTER_CATEGORIES, SECTION_LABELS } from "../constants";

export type FiltersSidebarProps = {
  activeFilter: FilterKey | null;
  onFilterSelect: (filterId: FilterKey | null) => void;
  getFilterCount: (key: FilterKey) => number;
  categories?: FilterCategory[];
  header?: ReactNode;
  footer?: ReactNode;
  /** When true, the sidebar renders as a thin rail. */
  collapsed?: boolean;
  /** Toggles the collapsed state (renders expand/collapse controls when provided). */
  onToggleCollapse?: () => void;
  /** Shows an "Overview" entry at the top (selected when activeFilter is null). */
  showOverviewItem?: boolean;
  /** Shows the SuperKudu-only "Known Companies" entry under ACCOUNTS. */
  showKnownCompanies?: boolean;
  /** Shows the categories marked `superKuduOnly` (badged "SuperKudu"). */
  showSuperKuduFilters?: boolean;
};

export const FiltersSidebar: FC<FiltersSidebarProps> = memo(
  ({
    activeFilter,
    onFilterSelect,
    getFilterCount,
    categories = FILTER_CATEGORIES,
    header,
    footer,
    collapsed = false,
    onToggleCollapse,
    showOverviewItem = false,
    showKnownCompanies = false,
    showSuperKuduFilters = false,
  }) => {
    const [search, setSearch] = useState("");
    const normalizedSearch = search.trim().toLowerCase();

    const showTargetOrganizations = useMemo(() => {
      if (!normalizedSearch) return true;
      const haystack = "search accounts targetorganizations".toLowerCase();
      return haystack.includes(normalizedSearch);
    }, [normalizedSearch]);

    const showUploadedAccounts = useMemo(() => {
      if (!normalizedSearch) return true;
      const haystack = "upload file csv accounts uploaded".toLowerCase();
      return haystack.includes(normalizedSearch);
    }, [normalizedSearch]);

    const showKnownCompaniesItem = useMemo(() => {
      if (!showKnownCompanies) return false;
      if (!normalizedSearch) return true;
      const haystack = "known companies hg id csv accounts superkudu";
      return haystack.includes(normalizedSearch);
    }, [showKnownCompanies, normalizedSearch]);

    const visibleCategories = useMemo(
      () =>
        showSuperKuduFilters
          ? categories
          : categories.filter((cat) => !cat.superKuduOnly),
      [categories, showSuperKuduFilters]
    );

    const filteredCategories = useMemo(() => {
      if (!normalizedSearch) return visibleCategories;
      return visibleCategories.filter((cat) => {
        const haystack = `${cat.label} ${cat.id}`.toLowerCase();
        return haystack.includes(normalizedSearch);
      });
    }, [visibleCategories, normalizedSearch]);

    const groupedCategories = useMemo(() => {
      const groups: Record<FilterSection, FilterCategory[]> = {
        ACCOUNTS: [],
        SCORING: [],
        INTENT: [],
        FIRMOGRAPHICS: [],
        TECHNOGRAPHICS: [],
        IT_SPEND: [],
        AI_SPEND: [],
        BUYER_INTENT: [],
        CONTACTS: [],
        COMPANY_CONFIG: [],
        EVENTS: [],
      };
      filteredCategories.forEach((cat) => {
        groups[cat.section].push(cat);
      });
      return groups;
    }, [filteredCategories]);

    const handleFilterClick = useCallback(
      (filterId: FilterKey, enabled: boolean) => {
        if (!enabled) return;
        // Always select the clicked filter — re-clicking an active item keeps it
        // open rather than toggling back to the Overview.
        onFilterSelect(filterId);
      },
      [onFilterSelect]
    );

    const hasResults =
      filteredCategories.length > 0 ||
      showTargetOrganizations ||
      showUploadedAccounts ||
      showKnownCompaniesItem;
    const showAccountsSection =
      showTargetOrganizations || showUploadedAccounts || showKnownCompaniesItem;

    // Number of filter categories that currently have applied values — used for
    // the badge on the collapsed rail.
    const activeFilterCount = useMemo(() => {
      let count = 0;
      visibleCategories.forEach((cat) => {
        if (getFilterCount(cat.id) > 0) count += 1;
      });
      if (getFilterCount("targetOrganizations") > 0) count += 1;
      if (getFilterCount("uploadedAccounts") > 0) count += 1;
      if (showKnownCompanies && getFilterCount("knownCompanies") > 0)
        count += 1;
      return count;
    }, [visibleCategories, getFilterCount, showKnownCompanies]);

    if (collapsed) {
      return (
        <Box
          sx={{
            width: 48,
            minWidth: 48,
            flexShrink: 0,
            bgcolor: "grey.50",
            borderRight: 1,
            borderColor: "divider",
            height: "100%",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            p: 0,
          }}
        >
          <Tooltip title="Show filters" placement="right">
            <IconButton
              size="small"
              onClick={onToggleCollapse}
              aria-label="Show filters"
              data-testid="filters-sidebar-expand-btn"
              sx={{ mt: 1 }}
            >
              <Badge
                badgeContent={activeFilterCount}
                color="primary"
                overlap="circular"
              >
                <FilterListIcon fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          width: 220,
          minWidth: 220,
          flexShrink: 0,
          bgcolor: "grey.50",
          borderRight: 1,
          borderColor: "divider",
          height: "100%",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {header}

        <Box sx={{ p: 2, pb: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography variant="subtitle2" fontWeight="bold">
              FILTERS
            </Typography>
            {onToggleCollapse && (
              <Button
                size="small"
                color="primary"
                onClick={onToggleCollapse}
                startIcon={<ChevronLeftIcon fontSize="small" />}
                data-testid="filters-sidebar-collapse-btn"
                sx={{ ml: 1, minWidth: 0, textTransform: "none" }}
              >
                Hide
              </Button>
            )}
          </Box>
          <TextField
            id="filter-sidebar-search-input"
            size="small"
            placeholder="Search filters..."
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setSearch("");
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      fontSize="small"
                      sx={{ color: "text.secondary" }}
                    />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: "background.paper",
              },
            }}
          />
        </Box>

        <Box sx={{ flex: 1, overflow: "auto" }}>
          {showOverviewItem && (
            <List dense disablePadding sx={{ pb: 1 }}>
              <ListItemButton
                selected={activeFilter === null}
                onClick={() => onFilterSelect(null)}
                data-testid="filter-sidebar-overview"
                sx={{
                  py: 0.75,
                  px: 2,
                  "&.Mui-selected": {
                    bgcolor: "primary.lighter",
                    borderLeft: 3,
                    borderColor: "primary.main",
                  },
                }}
              >
                <ListItemText
                  primary="Overview"
                  primaryTypographyProps={{
                    variant: "body2",
                    fontWeight: 600,
                  }}
                />
              </ListItemButton>
            </List>
          )}

          {!hasResults && (
            <Box sx={{ px: 2, py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No filters found
              </Typography>
            </Box>
          )}

          {showAccountsSection && (
            <>
              <Box sx={{ px: 2, py: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: "text.secondary",
                    letterSpacing: "0.5px",
                  }}
                >
                  ACCOUNTS
                </Typography>
              </Box>

              <List dense disablePadding sx={{ pb: 1 }}>
                {showTargetOrganizations && (
                  <ListItemButton
                    id="filter-sidebar-targetOrganizations"
                    selected={activeFilter === "targetOrganizations"}
                    onClick={() => onFilterSelect("targetOrganizations")}
                    sx={{
                      py: 0.75,
                      px: 2,
                      "&.Mui-selected": {
                        bgcolor: "primary.lighter",
                        borderLeft: 3,
                        borderColor: "primary.main",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor:
                          getFilterCount("targetOrganizations") > 0
                            ? "primary.main"
                            : "grey.400",
                        mr: 1.5,
                        flexShrink: 0,
                      }}
                    />

                    <ListItemText
                      primary="Search Companies"
                      primaryTypographyProps={{
                        variant: "body2",
                        fontWeight:
                          getFilterCount("targetOrganizations") > 0 ? 600 : 400,
                        color:
                          getFilterCount("targetOrganizations") > 0
                            ? "primary.dark"
                            : "text.primary",
                      }}
                    />
                  </ListItemButton>
                )}

                {showUploadedAccounts && (
                  <ListItemButton
                    id="filter-sidebar-uploadedAccounts"
                    selected={activeFilter === "uploadedAccounts"}
                    onClick={() => onFilterSelect("uploadedAccounts")}
                    sx={{
                      py: 0.75,
                      px: 2,
                      "&.Mui-selected": {
                        bgcolor: "primary.lighter",
                        borderLeft: 3,
                        borderColor: "primary.main",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor:
                          getFilterCount("uploadedAccounts") > 0
                            ? "primary.main"
                            : "grey.400",
                        mr: 1.5,
                        flexShrink: 0,
                      }}
                    />

                    <ListItemText
                      primary="Uploaded Accounts"
                      primaryTypographyProps={{
                        variant: "body2",
                        fontWeight:
                          getFilterCount("uploadedAccounts") > 0 ? 600 : 400,
                        color:
                          getFilterCount("uploadedAccounts") > 0
                            ? "primary.dark"
                            : "text.primary",
                      }}
                    />
                  </ListItemButton>
                )}

                {showKnownCompaniesItem && (
                  <ListItemButton
                    id="filter-sidebar-knownCompanies"
                    data-testid="filter-sidebar-known-companies-btn"
                    selected={activeFilter === "knownCompanies"}
                    onClick={() => onFilterSelect("knownCompanies")}
                    sx={{
                      py: 0.75,
                      px: 2,
                      "&.Mui-selected": {
                        bgcolor: "primary.lighter",
                        borderLeft: 3,
                        borderColor: "primary.main",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor:
                          getFilterCount("knownCompanies") > 0
                            ? "primary.main"
                            : "grey.400",
                        mr: 1.5,
                        flexShrink: 0,
                      }}
                    />

                    <ListItemText
                      primary="Known Companies"
                      primaryTypographyProps={{
                        variant: "body2",
                        fontWeight:
                          getFilterCount("knownCompanies") > 0 ? 600 : 400,
                        color:
                          getFilterCount("knownCompanies") > 0
                            ? "primary.dark"
                            : "text.primary",
                      }}
                    />
                    <Chip
                      label="SuperKudu"
                      size="small"
                      color="secondary"
                      variant="outlined"
                      data-testid="filter-sidebar-known-companies-superkudu-chip"
                      sx={{ height: 18, fontSize: 10, ml: 1 }}
                    />
                  </ListItemButton>
                )}
              </List>
            </>
          )}

          {(Object.keys(groupedCategories) as FilterSection[]).map(
            (section) => {
              const sectionCategories = groupedCategories[section];
              if (sectionCategories.length === 0) return null;

              return (
                <Box key={section} mb={1}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      px: 2,
                      py: 1,
                      fontWeight: 700,
                      color: "text.secondary",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {SECTION_LABELS[section]}
                  </Typography>

                  <List dense disablePadding>
                    {sectionCategories.map((category) => {
                      const isActive = activeFilter === category.id;
                      const hasValues = getFilterCount(category.id) > 0;

                      const button = (
                        <ListItemButton
                          key={category.id}
                          id={`filter-sidebar-${category.id}`}
                          selected={isActive}
                          disabled={!category.enabled}
                          onClick={() =>
                            handleFilterClick(category.id, category.enabled)
                          }
                          sx={{
                            py: 0.75,
                            px: 2,
                            "&.Mui-selected": {
                              bgcolor: "primary.lighter",
                              borderLeft: 3,
                              borderColor: "primary.main",
                            },
                            "&.Mui-disabled": {
                              opacity: 0.5,
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              bgcolor: hasValues ? "primary.main" : "grey.400",
                              mr: 1.5,
                              flexShrink: 0,
                            }}
                          />

                          <ListItemText
                            primary={category.label}
                            primaryTypographyProps={{
                              variant: "body2",
                              fontWeight: hasValues ? 500 : 400,
                            }}
                          />
                          {category.superKuduOnly && (
                            <Chip
                              label="SuperKudu"
                              size="small"
                              color="secondary"
                              variant="outlined"
                              data-testid={`filter-sidebar-${category.id}-superkudu-chip`}
                              sx={{ height: 18, fontSize: 10, ml: 1 }}
                            />
                          )}
                        </ListItemButton>
                      );

                      if (!category.enabled && category.disabledReason) {
                        return (
                          <Tooltip
                            key={category.id}
                            title={category.disabledReason}
                            placement="right"
                            arrow
                          >
                            <span>{button}</span>
                          </Tooltip>
                        );
                      }

                      return button;
                    })}
                  </List>
                </Box>
              );
            }
          )}
        </Box>

        {footer && (
          <>
            <Divider />
            <Box
              sx={{
                p: 2,
                minHeight: 8,
                flexShrink: 0,
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
              }}
            >
              {footer}
            </Box>
          </>
        )}
      </Box>
    );
  }
);

FiltersSidebar.displayName = "FiltersSidebar";
